/**
 * StatCard - Animated stat display cards with micro-interactions
 *
 * Features:
 * - Smooth fade-in animations with stagger support
 * - Subtle hover lift effect
 * - Count-up animation on numbers
 * - Skeleton loading state
 */

import { useEffect, useRef, useState } from "react";
import { motion, useSpring, useTransform, useInView } from "framer-motion";

interface StatCardProps {
  label: string;
  value: string | number;
  index?: number; // For stagger animation
  percentChange?: number | null;
  valueClassName?: string; // Optional override for value text color
}

// Parse number from formatted string
function parseDisplayValue(value: string | number): number {
  if (typeof value === "number") return value;
  const cleaned = value.replace(/[$,%KM]/g, "").replace(/,/g, "");
  let num = parseFloat(cleaned) || 0;
  if (value.includes("M")) num *= 1000000;
  if (value.includes("K")) num *= 1000;
  return num;
}

// Format number back to display format
function formatToDisplay(num: number, originalValue: string): string {
  if (originalValue.includes("M")) {
    return `$${(num / 1000000).toFixed(2)}M`;
  }
  if (originalValue.includes("K")) {
    return `$${(num / 1000).toFixed(1)}K`;
  }
  if (originalValue.includes("%")) {
    return `${num.toFixed(1)}%`;
  }
  if (originalValue.startsWith("$")) {
    return `$${Math.round(num).toLocaleString("en-US")}`;
  }
  return Math.round(num).toLocaleString("en-US");
}

// Animated number component - only animates on initial load, not filter changes
function AnimatedValue({ value, className }: { value: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-20px" });
  const targetValue = parseDisplayValue(value);
  const hasAnimatedOnce = useRef(false);

  const spring = useSpring(0, {
    stiffness: 60,
    damping: 25,
  });

  const display = useTransform(spring, (current) => formatToDisplay(current, value));
  const [displayValue, setDisplayValue] = useState(formatToDisplay(0, value));

  // Only animate the first time the component becomes visible
  useEffect(() => {
    if (isInView && !hasAnimatedOnce.current) {
      hasAnimatedOnce.current = true;
      spring.set(targetValue);
    }
  }, [isInView, targetValue, spring]);

  useEffect(() => {
    const unsubscribe = display.on("change", (v) => setDisplayValue(v));
    return unsubscribe;
  }, [display]);

  // When value changes after initial animation, update instantly (no animation)
  useEffect(() => {
    if (hasAnimatedOnce.current) {
      spring.jump(targetValue);
    }
  }, [value, spring, targetValue]);

  return (
    <span ref={ref} className={className}>
      {displayValue}
    </span>
  );
}

export function StatCard({ label, value, index = 0, percentChange, valueClassName }: StatCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.4,
        delay: 0.3 + index * 0.05,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      whileHover={{
        y: -2,
        boxShadow: "0 8px 30px -10px rgba(0, 0, 0, 0.15)",
        transition: { duration: 0.2 },
      }}
      className="
        bg-card rounded-xl border border-border p-3 md:p-4
        transition-colors duration-300
        hover:border-border/80 hover:bg-card/80
        backdrop-blur-sm
      "
    >
      <div className="space-y-1 md:space-y-1.5">
        <motion.p
          className="text-[10px] md:text-xs font-medium text-muted-foreground uppercase tracking-wider"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 + index * 0.05 }}
        >
          {label}
        </motion.p>
        <div className="flex items-baseline gap-1.5 md:gap-2">
          <AnimatedValue
            value={String(value)}
            className={`text-lg md:text-xl font-bold ${valueClassName || "text-foreground"}`}
          />
          {percentChange != null && (
            <motion.span
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + index * 0.05 }}
              className={`text-xs font-semibold ${
                percentChange > 0
                  ? "text-emerald-400"
                  : percentChange < 0
                  ? "text-red-400"
                  : "text-muted-foreground"
              }`}
            >
              {percentChange > 0 ? "+" : ""}
              {percentChange.toFixed(2)}%
            </motion.span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Skeleton version
export function StatCardSkeleton({ index = 0 }: { index?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.03 }}
      className="relative overflow-hidden bg-card rounded-xl border border-border p-4"
    >
      <div className="space-y-2">
        <div className="h-3 w-20 rounded bg-muted animate-pulse" />
        <div className="h-6 w-16 rounded bg-muted animate-pulse" />
      </div>
      <motion.div
        className="absolute inset-0"
        animate={{ x: ["-100%", "100%"] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        style={{
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
        }}
      />
    </motion.div>
  );
}

/**
 * WideStatCard - Larger stat card for 2-column layout
 */
export function WideStatCard({ label, value, index = 0 }: StatCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.4,
        delay: 0.4 + index * 0.05,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      whileHover={{
        y: -2,
        boxShadow: "0 8px 30px -10px rgba(0, 0, 0, 0.15)",
        transition: { duration: 0.2 },
      }}
      className="
        bg-card rounded-xl border border-border p-4
        transition-colors duration-300
        hover:border-border/80 hover:bg-card/80
        backdrop-blur-sm
      "
    >
      <div className="space-y-1.5">
        <motion.p
          className="text-xs font-medium text-muted-foreground uppercase tracking-wider"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 + index * 0.05 }}
        >
          {label}
        </motion.p>
        <AnimatedValue
          value={String(value)}
          className="text-xl font-bold text-foreground block"
        />
      </div>
    </motion.div>
  );
}

/**
 * AdditionalStatCard - Stat card with description text
 */
interface AdditionalStatCardProps {
  label: string;
  value: string | number;
  description?: string;
  index?: number;
}

export function AdditionalStatCard({ label, value, description, index = 0 }: AdditionalStatCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.4,
        delay: 0.5 + index * 0.06,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      whileHover={{
        y: -3,
        boxShadow: "0 12px 40px -10px rgba(0, 0, 0, 0.2)",
        transition: { duration: 0.2 },
      }}
      className="
        bg-card rounded-xl border-2 border-border p-4 md:p-6
        transition-colors duration-300
        hover:border-border/80
        backdrop-blur-sm
      "
    >
      <div className="space-y-1.5 md:space-y-2">
        <motion.p
          className="text-xs md:text-sm font-semibold text-muted-foreground uppercase tracking-wider"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 + index * 0.06 }}
        >
          {label}
        </motion.p>
        <AnimatedValue
          value={String(value)}
          className="text-2xl md:text-3xl font-bold text-foreground block"
        />
        {description && (
          <motion.p
            className="text-xs text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 + index * 0.06 }}
          >
            {description}
          </motion.p>
        )}
      </div>
    </motion.div>
  );
}

// Skeleton for AdditionalStatCard
export function AdditionalStatCardSkeleton({ index = 0 }: { index?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.03 }}
      className="relative overflow-hidden bg-card rounded-xl border-2 border-border p-6"
    >
      <div className="space-y-3">
        <div className="h-4 w-32 rounded bg-muted animate-pulse" />
        <div className="h-8 w-24 rounded bg-muted animate-pulse" />
        <div className="h-3 w-40 rounded bg-muted/70 animate-pulse" />
      </div>
      <motion.div
        className="absolute inset-0"
        animate={{ x: ["-100%", "100%"] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        style={{
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
        }}
      />
    </motion.div>
  );
}
